import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Strategy } from '@/types/strategy';
import {
  getOrCreateTrailingEngine,
  getTrailingEngine,
  normalizeTrailingParams,
  RESET_TRAILING_STOP_PARAMS,
  type TrailingStopParams,
  type TrailingStopSnapshot,
  type TrackedPosition,
} from '@/lib/trailing-stop-engine';
import { useTrailingSessionPersistence } from '@/hooks/useTrailingSessionPersistence';
import { useCtraderStream } from '@/hooks/useCtraderStream';
import { ensureStream, releaseStream } from '@/lib/background-stream-manager';

// ─── Types ──────────────────────────────────────────────────────

interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
}

// ─── Helpers ────────────────────────────────────────────────────

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

function formatPips(val: number): string {
  return val >= 0 ? `+${val.toFixed(1)}` : val.toFixed(1);
}

// ─── Component ──────────────────────────────────────────────────

interface TrailingStopMonitorProps {
  strategy: Strategy;
  ctraderAccounts?: CtraderAccount[];
  isActive: boolean;
  initialAccountId?: string;
}

export default function TrailingStopMonitor({
  strategy,
  ctraderAccounts = [],
  isActive,
  initialAccountId,
}: TrailingStopMonitorProps) {
  const strategyId = strategy.id || '';
  const defaultParams = normalizeTrailingParams(strategy.trailingStopParams);

  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [engineState, setEngineState] = useState<TrailingStopSnapshot | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [localParams, setLocalParams] = useState<TrailingStopParams>(defaultParams);
  // Draft params being edited in the Settings panel. These are NOT applied to
  // the engine or persisted until the user clicks "Apply".
  const [draftParams, setDraftParams] = useState<TrailingStopParams>(defaultParams);
  // Draft strings for numeric inputs so the user can clear a field and retype
  // without it snapping back to the previous value.
  const [numericDrafts, setNumericDrafts] = useState<Record<string, string>>({
    profitReach: String(defaultParams.profitReach),
    step: String(defaultParams.step),
    takeProfit: String(defaultParams.takeProfit),
    stopLoss: String(defaultParams.stopLoss),
    takeProfitPrice: defaultParams.takeProfitPrice !== null ? String(defaultParams.takeProfitPrice) : '',
    stopLossPrice: defaultParams.stopLossPrice !== null ? String(defaultParams.stopLossPrice) : '',
  });
  const [initialized, setInitialized] = useState(false);
  const [paramsLoaded, setParamsLoaded] = useState(false);
  const [isEngineRunning, setIsEngineRunning] = useState(false);
  const [closingPositions, setClosingPositions] = useState<Set<string>>(new Set());
  const [closingAll, setClosingAll] = useState(false);
  const [confirmCloseAll, setConfirmCloseAll] = useState(false);

  // Apply incoming params (from restore, another device, OR the "Apply" button)
  // to the engine-bound local state, keeping draft + numeric strings in sync too.
  const applyParams = useCallback((params: TrailingStopParams) => {
    setLocalParams(params);
    setDraftParams(params);
    setNumericDrafts({
      profitReach: String(params.profitReach),
      step: String(params.step),
      takeProfit: String(params.takeProfit),
      stopLoss: String(params.stopLoss),
      takeProfitPrice: params.takeProfitPrice !== null ? String(params.takeProfitPrice) : '',
      stopLossPrice: params.stopLossPrice !== null ? String(params.stopLossPrice) : '',
    });
  }, []);

  // Whether the Settings draft differs from the currently-applied params.
  const dirty =
    draftParams.mode !== localParams.mode ||
    draftParams.profitReach !== localParams.profitReach ||
    draftParams.step !== localParams.step ||
    draftParams.takeProfit !== localParams.takeProfit ||
    draftParams.stopLoss !== localParams.stopLoss ||
    draftParams.usePriceSLTP !== localParams.usePriceSLTP ||
    draftParams.takeProfitPrice !== localParams.takeProfitPrice ||
    draftParams.stopLossPrice !== localParams.stopLossPrice;

  const { scheduleSave, loadSessionWithFallback } = useTrailingSessionPersistence(
    strategyId,
    selectedAccountId,
    applyParams,
  );
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const startAttemptedRef = useRef(false);
  // Tracks the last seen (accountId, resetSignal) so we only mirror a reset
  // when the signal bumps on the SAME account (not on account switches).
  const lastResetRef = useRef<{ accountId: string; signal: number } | null>(null);

  // ─── Stream for real-time prices (visible on this page) ──────

  const streamSymbolIds = (engineState?.positions
    ? [...new Set(Object.values(engineState.positions).map((p) => p.symbolId))]
    : []);

  const streamDigits: Record<number, number> = {};
  if (engineState?.positions) {
    for (const p of Object.values(engineState.positions)) {
      streamDigits[p.symbolId] = p.digits;
    }
  }

  const numAccountId = selectedAccountId ? parseInt(selectedAccountId, 10) : null;

  // Use the stream hook — ensures a local stream while on this page
  useCtraderStream({
    accountId: isNaN(numAccountId as number) ? null : numAccountId,
    symbolIds: streamSymbolIds,
    periods: [],
    symbolDigits: streamDigits,
    enabled: isActive && !!numAccountId && streamSymbolIds.length > 0,
  });

  // Additionally ensure background stream survives navigation away from this page
  const streamEnsuredRef = useRef(false);
  useEffect(() => {
    if (isActive && numAccountId && !isNaN(numAccountId) && streamSymbolIds.length > 0) {
      ensureStream(numAccountId, streamSymbolIds, streamDigits);
      streamEnsuredRef.current = true;
    }
    return () => {
      if (streamEnsuredRef.current && numAccountId && !isNaN(numAccountId)) {
        releaseStream(numAccountId);
        streamEnsuredRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, numAccountId]);

  // ─── Restore persisted settings (from Supabase) ─────────────
  // Runs per selected account so the COMPOSITE key (strategyId__account_X)
  // is loaded correctly — not the legacy key, which would reset the engine
  // back to strategy defaults and drop the user's saved per-account params.

  useEffect(() => {
    let cancelled = false;
    setParamsLoaded(false);
    setInitialized(false);
    loadSessionWithFallback().then((saved) => {
      if (cancelled) return;
      if (saved && saved.localParams) {
        applyParams(saved.localParams);
      }
      setParamsLoaded(true);
    });
    return () => { cancelled = true; };
  }, [selectedAccountId, loadSessionWithFallback, applyParams]);

  // ─── Auto-select account ──────────────────────────────────────

  useEffect(() => {
    if (ctraderAccounts.length === 0) return;

    // ─── URL initialAccountId takes PRIORITY over engine's current account ───
    // When the user explicitly navigates with ?accountId=XXX, that's what they want to see.
    if (initialAccountId) {
      const match = ctraderAccounts.find((a) => String(a.account_id) === String(initialAccountId));
      if (match) {
        setSelectedAccountId(match.account_id);
        return;
      }
    }

    // Check persisted session
    loadSessionWithFallback().then((saved) => {
      if (saved && saved.selectedAccountId) {
        const match = ctraderAccounts.find((a) => String(a.account_id) === String(saved.selectedAccountId));
        if (match) {
          setSelectedAccountId(match.account_id);
          return;
        }
      }

      if (strategy.active_accounts && strategy.active_accounts.length > 0) {
        const firstActive = strategy.active_accounts[0];
        const match = ctraderAccounts.find((a) => String(a.account_id) === String(firstActive));
        if (match) {
          setSelectedAccountId(match.account_id);
          return;
        }
      }

      setSelectedAccountId(ctraderAccounts[0].account_id);
    });
  }, [ctraderAccounts, strategyId, strategy.active_accounts, initialAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Subscribe to engine ──────────────────────────────────────

  useEffect(() => {
    if (!strategyId || !selectedAccountId || !paramsLoaded) return;

    const engine = getOrCreateTrailingEngine(strategyId, selectedAccountId, localParams);
    setIsEngineRunning(engine.isRunning);

    unsubscribeRef.current = engine.subscribe((s) => {
      setEngineState(s);
      setIsEngineRunning(s.isRunning);
    });

    setInitialized(true);

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [strategyId, selectedAccountId, paramsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sync localParams to engine ───────────────────────────────

  useEffect(() => {
    if (!strategyId || !selectedAccountId || !paramsLoaded) return;
    const engine = getTrailingEngine(strategyId, selectedAccountId);
    if (engine) engine.setParams(localParams);
  }, [localParams, strategyId, selectedAccountId, paramsLoaded]);

  // ─── Mirror engine "reset to defaults" into local state + persistence ───
  // When the engine resets trailing params (no open positions), it bumps
  // `paramsResetSignal` on the snapshot. We mirror that reset into localParams
  // and persist it, so the UI + Supabase reflect the fresh-trade defaults.
  useEffect(() => {
    const signal = engineState?.paramsResetSignal ?? 0;
    const prev = lastResetRef.current;
    const sameAccount = prev?.accountId === selectedAccountId;
    const signalChanged = !sameAccount || prev.signal !== signal;
    lastResetRef.current = { accountId: selectedAccountId, signal };

    if (sameAccount && prev.signal !== signal) {
      const resetParams = normalizeTrailingParams({
        ...RESET_TRAILING_STOP_PARAMS,
        mode: localParams.mode,
      });
      applyParams(resetParams);
      scheduleSave(resetParams);
    }
  }, [engineState?.paramsResetSignal, selectedAccountId, localParams.mode, applyParams, scheduleSave]);

  // ─── Start engine ─────────────────────────────────────────────

  const startEngine = useCallback(async () => {
    if (!selectedAccountId || !strategyId) return;

    const engine = getOrCreateTrailingEngine(strategyId, selectedAccountId, localParams);

    if (engine.isRunning && engine.selectedAccountId === selectedAccountId) {
      return;
    }

    if (engine.selectedAccountId !== selectedAccountId || !engine.isRunning) {
      await engine.init(selectedAccountId);
      engine.start();
    }
  }, [selectedAccountId, strategyId, localParams]);

  useEffect(() => {
    if (selectedAccountId && initialized && isActive && !startAttemptedRef.current) {
      startAttemptedRef.current = true;
      startEngine();
    }
  }, [selectedAccountId, initialized, isActive, startEngine]);

  useEffect(() => {
    startAttemptedRef.current = false;
  }, [selectedAccountId]);

  // ─── Handle isActive changes ──────────────────────────────────

  useEffect(() => {
    if (!strategyId || !initialized) return;
    const engine = getTrailingEngine(strategyId, selectedAccountId);
    if (!engine) return;

    if (isActive) {
      if (!engine.isRunning && selectedAccountId) {
        startEngine();
      }
    } else {
      engine.stop();
    }
  }, [isActive, strategyId, initialized, selectedAccountId, startEngine]);

  // ─── Settings editing (draft-only; nothing applied until "Apply") ───

  const handleNumericChange = useCallback((key: 'profitReach' | 'step' | 'takeProfit' | 'stopLoss', raw: string) => {
    setNumericDrafts((prev) => ({ ...prev, [key]: raw }));
    const num = parseInt(raw, 10);
    if (!isNaN(num)) {
      setDraftParams((prev) => ({ ...prev, [key]: num }));
    }
  }, []);

  // Price values use decimals (e.g. 1.2550) — parseFloat, empty → null.
  const handlePriceChange = useCallback((key: 'takeProfitPrice' | 'stopLossPrice', raw: string) => {
    setNumericDrafts((prev) => ({ ...prev, [key]: raw }));
    if (raw.trim() === '') {
      setDraftParams((prev) => ({ ...prev, [key]: null }));
      return;
    }
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      setDraftParams((prev) => ({ ...prev, [key]: num }));
    }
  }, []);

  const handleUsePriceToggle = useCallback(() => {
    setDraftParams((prev) => ({ ...prev, usePriceSLTP: !prev.usePriceSLTP }));
  }, []);

  const handleModeChange = useCallback((mode: TrailingStopParams['mode']) => {
    setDraftParams((prev) => ({ ...prev, mode }));
  }, []);

  // Commit the draft into applied params (engine + persistence).
  const handleApply = useCallback(() => {
    const next = normalizeTrailingParams(draftParams);
    applyParams(next);
    scheduleSave(next);
  }, [draftParams, applyParams, scheduleSave]);

  // Discard draft edits, revert to currently-applied params.
  const handleResetDraft = useCallback(() => {
    setDraftParams(localParams);
    setNumericDrafts({
      profitReach: String(localParams.profitReach),
      step: String(localParams.step),
      takeProfit: String(localParams.takeProfit),
      stopLoss: String(localParams.stopLoss),
      takeProfitPrice: localParams.takeProfitPrice !== null ? String(localParams.takeProfitPrice) : '',
      stopLossPrice: localParams.stopLossPrice !== null ? String(localParams.stopLossPrice) : '',
    });
  }, [localParams]);

  const openSettings = useCallback(() => {
    handleResetDraft();
    setShowSettings(true);
  }, [handleResetDraft]);

  const closeSettings = useCallback(() => setShowSettings(false), []);

  // Cancel: discard draft and close the panel without applying or saving.
  const handleCancel = useCallback(() => {
    handleResetDraft();
    setShowSettings(false);
  }, [handleResetDraft]);

  // ─── Close position handlers ─────────────────────────────────

  const handleClosePosition = useCallback(async (positionId: string) => {
    const engine = getTrailingEngine(strategyId, selectedAccountId);
    if (!engine) return;

    setClosingPositions((prev) => new Set(prev).add(positionId));
    try {
      await engine.closePositionById(positionId);
    } finally {
      setClosingPositions((prev) => {
        const next = new Set(prev);
        next.delete(positionId);
        return next;
      });
    }
  }, [strategyId, selectedAccountId]);

  const handleCloseAll = useCallback(async () => {
    const engine = getTrailingEngine(strategyId, selectedAccountId);
    if (!engine) return;

    setClosingAll(true);
    setConfirmCloseAll(false);
    try {
      await engine.closeAllPositions();
    } finally {
      setClosingAll(false);
    }
  }, [strategyId, selectedAccountId]);

  // ─── Derived data ─────────────────────────────────────────────

  const positions = engineState?.positions || {};
  const positionList = Object.values(positions);
  const lastTick = engineState?.lastTick || 0;
  const streamActive = engineState?.streamActive ?? false;
  const isLoading = engineState?.loading ?? false;
  const errorMsg = engineState?.lastError || '';
  const depositCurrency = engineState?.depositCurrency || '';

  const currentAccount = ctraderAccounts.find((a) => String(a.account_id) === String(selectedAccountId));
  const currentMode = engineState?.currentMode || localParams.mode;

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* ─── Header Bar ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={cn(
            'w-2.5 h-2.5 rounded-full',
            isEngineRunning ? 'bg-emerald-400 animate-pulse' : isLoading ? 'bg-amber-400 animate-pulse' : 'bg-foreground-300'
          )}></div>
          <span className="text-xs font-medium text-foreground-700">
            {isEngineRunning ? 'Trailing Active' : isLoading ? 'Loading positions...' : isActive ? 'Starting...' : 'Trailing Stopped'}
          </span>
          {lastTick > 0 && (
            <span className="text-[10px] text-foreground-400">
              Last tick: {new Date(lastTick).toLocaleTimeString('en-US')}
            </span>
          )}
          <span className="text-[10px] text-foreground-400">
            · {positionList.length} position{positionList.length !== 1 ? 's' : ''}
          </span>
          {isEngineRunning && (
            <span className={cn(
              'text-[10px] px-2 py-0.5 rounded-full font-medium',
              streamActive
                ? 'text-emerald-500 bg-emerald-500/10'
                : 'text-amber-500 bg-amber-500/10'
            )}>
              <i className={cn(streamActive ? 'ri-flashlight-line' : 'ri-history-line', 'mr-1')}></i>
              {streamActive ? 'Real-time' : 'Polling fallback'}
            </span>
          )}
          {isEngineRunning && (
            <span className={cn(
              'text-[10px] px-2 py-0.5 rounded-full font-medium',
              currentMode === 'full'
                ? 'text-accent-500 bg-accent-500/10'
                : currentMode === 'simple'
                  ? 'text-secondary-500 bg-secondary-500/10'
                  : 'text-foreground-500 bg-foreground-100'
            )}>
              <i className={cn(
                currentMode === 'full' ? 'ri-rocket-line' : currentMode === 'simple' ? 'ri-shield-check-line' : 'ri-settings-3-line',
                'mr-1'
              )}></i>
              {currentMode === 'full' ? 'Full Trail' : currentMode === 'simple' ? 'Simple Trail' : 'Setup Only'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* ─── Fixed Account Display ─── */}
          {ctraderAccounts.length > 0 && selectedAccountId ? (
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0"></div>
              <span className="text-[10px] text-foreground-500">Bot Account:</span>
              <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">
                {currentAccount?.account_name || selectedAccountId}
              </span>
              <span className="text-[10px] text-foreground-400">#{selectedAccountId}</span>
            </div>
          ) : !selectedAccountId ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-400">
              <i className="ri-error-warning-line"></i>
              No account selected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs text-foreground-400">
              <i className="ri-error-warning-line"></i>
              No account connected
            </span>
          )}

          <button
            type="button"
            onClick={openSettings}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 border rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap',
              showSettings
                ? 'bg-accent-500/10 border-accent-500/30 text-accent-600'
                : 'bg-background-100 hover:bg-background-200/70 border-background-200/70 text-foreground-600'
            )}
          >
            <i className="ri-settings-3-line"></i>
            Settings
          </button>
        </div>
      </div>

      {/* ─── Error Banner ─── */}
      {errorMsg && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-400 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-heading font-semibold text-red-400 mb-1">Position Load Error</h4>
            <p className="text-xs text-foreground-600 break-words">{errorMsg}</p>
          </div>
          <button
            type="button"
            onClick={() => startEngine()}
            className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-refresh-line mr-1"></i>
            Retry
          </button>
        </div>
      )}

      {/* ─── Settings Panel ─── */}
      {showSettings && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-heading font-semibold text-foreground-900">Trailing Stop Settings</h4>
            <button
              type="button"
              onClick={closeSettings}
              className="text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          {/* ─── Mode Toggle ─── */}
          <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
            <div className="flex-1">
              <span className="text-xs font-medium text-foreground-700 block mb-1">Trailing Mode</span>
              <span className="text-[10px] text-foreground-500">
                {draftParams.mode === 'setupOnly'
                  ? 'Only set initial SL & TP on open positions (no trailing)'
                  : draftParams.mode === 'simple'
                    ? 'SL → breakeven +1 pip when profit reaches target (no stepping)'
                    : 'SL → breakeven +1 pip, then trails step-by-step as profit grows'}
              </span>
            </div>
            <div className="flex items-center bg-background-200/70 rounded-full p-0.5">
              <button
                type="button"
                onClick={() => handleModeChange('setupOnly')}
                className={cn(
                  'px-3 py-1.5 rounded-full text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap',
                  draftParams.mode === 'setupOnly'
                    ? 'bg-background-50 text-foreground-900 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                )}
              >
                Setup Only
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('simple')}
                className={cn(
                  'px-3 py-1.5 rounded-full text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap',
                  draftParams.mode === 'simple'
                    ? 'bg-secondary-500 text-background-50 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                )}
              >
                Simple
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('full')}
                className={cn(
                  'px-3 py-1.5 rounded-full text-[11px] font-medium transition-all cursor-pointer whitespace-nowrap',
                  draftParams.mode === 'full'
                    ? 'bg-accent-500 text-background-50 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                )}
              >
                Full
              </button>
            </div>
          </div>

          {/* ─── Price Value Mode Toggle ─── */}
          <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
            <div className="flex-1">
              <span className="text-xs font-medium text-foreground-700 block mb-1">TP/SL by Price Value</span>
              <span className="text-[10px] text-foreground-500">
                Fill exact price levels (e.g. EURUSD TP 1.2550) instead of pips
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={draftParams.usePriceSLTP}
              onClick={handleUsePriceToggle}
              className="flex items-center gap-2 cursor-pointer shrink-0"
            >
              <span className={cn(
                'w-5 h-5 rounded-md border flex items-center justify-center transition-colors',
                draftParams.usePriceSLTP
                  ? 'bg-primary-500 border-primary-500'
                  : 'border-background-300/60 bg-background-50'
              )}>
                {draftParams.usePriceSLTP && <i className="ri-check-line text-background-50 text-xs"></i>}
              </span>
              <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">Price mode</span>
            </button>
          </div>

          {draftParams.usePriceSLTP && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">Take Profit (price)</label>
                <input
                  type="number"
                  step="any"
                  value={numericDrafts.takeProfitPrice}
                  onChange={(e) => handlePriceChange('takeProfitPrice', e.target.value)}
                  placeholder="e.g. 1.2550"
                  className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">Stop Loss (price)</label>
                <input
                  type="number"
                  step="any"
                  value={numericDrafts.stopLossPrice}
                  onChange={(e) => handlePriceChange('stopLossPrice', e.target.value)}
                  placeholder="e.g. 1.2450"
                  className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">Profit Reach (pips)</label>
              <input
                type="number"
                value={numericDrafts.profitReach}
                onChange={(e) => handleNumericChange('profitReach', e.target.value)}
                className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none"
              />
              <span className="text-[10px] text-foreground-400 mt-0.5 block">Hit → SL = entry</span>
            </div>
            <div>
              <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">Step (pips)</label>
              <input
                type="number"
                value={numericDrafts.step}
                onChange={(e) => handleNumericChange('step', e.target.value)}
                className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none"
              />
              <span className="text-[10px] text-foreground-400 mt-0.5 block">Per trailing increment</span>
            </div>
            <div>
              <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">Take Profit (pips)</label>
              <input
                type="number"
                value={numericDrafts.takeProfit}
                onChange={(e) => handleNumericChange('takeProfit', e.target.value)}
                disabled={draftParams.usePriceSLTP}
                className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">Stop Loss (pips)</label>
              <input
                type="number"
                value={numericDrafts.stopLoss}
                onChange={(e) => handleNumericChange('stopLoss', e.target.value)}
                disabled={draftParams.usePriceSLTP}
                className="w-full px-2.5 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
              />
              <span className="text-[10px] text-foreground-400 mt-0.5 block">Initial SL offset</span>
            </div>
          </div>

          {/* ─── Apply / Reset actions ─── */}
          <div className="flex items-center justify-end gap-2 pt-1">
            {dirty && (
              <span className="text-[10px] text-amber-500 mr-auto inline-flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                Unsaved changes
              </span>
            )}
            <button
              type="button"
              onClick={handleResetDraft}
              disabled={!dirty}
              className="px-3 py-2 bg-background-100 hover:bg-background-200/70 border border-background-200/70 rounded-lg text-xs text-foreground-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-2 bg-background-100 hover:bg-background-200/70 border border-background-200/70 rounded-lg text-xs text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!dirty}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-check-line mr-1"></i>
              Apply
            </button>
          </div>
        </div>
      )}

      {/* ─── Loading State ─── */}
      {isLoading && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-4">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">Loading Positions</h4>
          <p className="text-xs text-foreground-500">
            Fetching open positions from cTrader for account {currentAccount?.account_name || selectedAccountId}...
          </p>
        </div>
      )}

      {/* ─── Positions Table ─── */}
      {!isLoading && positionList.length === 0 && !errorMsg && (
        <div className="bg-background-50 rounded-xl border border-dashed border-background-300/60 p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-200/70 flex items-center justify-center mx-auto mb-4">
            <i className="ri-stop-mini-line text-foreground-400 text-2xl"></i>
          </div>
          <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">No Open Positions</h4>
          <p className="text-xs text-foreground-500 mb-4 max-w-sm mx-auto">
            {isEngineRunning
              ? 'Trailing stop is active and monitoring. Positions will appear here automatically when detected.'
              : isActive
                ? 'The engine will start monitoring positions on the bot account automatically.'
                : 'Activate the bot to start monitoring positions on the connected account.'}
          </p>
          {!isEngineRunning && isActive && selectedAccountId && (
            <button
              type="button"
              onClick={() => startEngine()}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill"></i>
              Start Monitoring
            </button>
          )}
        </div>
      )}

      {!isLoading && positionList.length > 0 && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className={cn(
                'text-sm',
                streamActive ? 'ri-flashlight-line text-emerald-400' : 'ri-stop-mini-line text-amber-400'
              )}></i>
              <span className="text-xs font-medium text-foreground-700">
                Tracking {positionList.length} position{positionList.length > 1 ? 's' : ''}
              </span>
              <span className="text-[10px] text-foreground-400">
                {streamActive ? '· tick-by-tick' : '· fallback polling'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-foreground-400">
                Auto-detected from executed orders
              </span>
              {positionList.length > 1 && (
                confirmCloseAll ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setConfirmCloseAll(false)}
                      className="px-2.5 py-1 bg-background-100 border border-background-200/70 rounded text-[10px] text-foreground-600 hover:bg-background-200/50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseAll}
                      disabled={closingAll}
                      className="px-2.5 py-1 bg-red-500 hover:bg-red-600 text-background-50 rounded text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {closingAll ? (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-loader-2-line animate-spin"></i>
                          Closing...
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-alert-line"></i>
                          Confirm Close All ({positionList.length})
                        </span>
                      )}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmCloseAll(true)}
                    disabled={closingAll}
                    className="px-2.5 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    <i className="ri-close-circle-line mr-1"></i>
                    Close All ({positionList.length})
                  </button>
                )
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100">
                  <th className="text-left px-3 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Symbol</th>
                  <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Dir</th>
                  <th className="text-right px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Vol</th>
                  <th className="text-right px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Entry</th>
                  <th className="text-right px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Current</th>
                  <th className="text-right px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">SL</th>
                  <th className="text-right px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">TP</th>
                  <th className="text-right px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Pips</th>
                  <th className="text-center px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Trail Step</th>
                  <th className="text-center px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Action</th>
                </tr>
              </thead>
              <tbody>
                {positionList.map((pos: TrackedPosition) => (
                  <tr
                    key={pos.positionId}
                    className="border-b border-background-200/50 hover:bg-background-100/50 transition-colors"
                  >
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono font-semibold text-foreground-900">{pos.symbolName}</span>
                        {!pos.isForex && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-foreground-100 text-foreground-500" title="Non-Forex — trailing skipped">
                            SKIP
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2.5">
                      <span className={cn(
                        'px-1.5 py-0.5 rounded text-[10px] font-bold',
                        pos.direction === 'buy'
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-red-500/10 text-red-400'
                      )}>
                        {pos.direction.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-right px-2 py-2.5 font-mono text-foreground-600 text-[10px]">
                      {(pos.volume ?? pos.rawVolume / 1000).toFixed(2)}
                    </td>
                    <td className="text-right px-2 py-2.5 font-mono text-foreground-700">
                      {pos.entryPrice.toFixed(pos.digits)}
                    </td>
                    <td className="text-right px-2 py-2.5 font-mono text-foreground-900">
                      {pos.currentPrice.toFixed(pos.digits)}
                    </td>
                    <td className="text-right px-2 py-2.5 font-mono text-foreground-600">
                      {pos.currentSL !== null ? pos.currentSL.toFixed(pos.digits) : '\u2014'}
                    </td>
                    <td className="text-right px-2 py-2.5 font-mono text-foreground-600">
                      {pos.currentTP !== null ? pos.currentTP.toFixed(pos.digits) : '\u2014'}
                    </td>
                    <td className={cn(
                      'text-right px-2 py-2.5 font-mono text-[10px]',
                      pos.profitPips >= 0 ? 'text-emerald-400' : 'text-red-400'
                    )}>
                      {formatPips(pos.profitPips)}
                    </td>
                    <td className="text-center px-2 py-2.5">
                      {!pos.isForex ? (
                        <span className="text-[10px] text-foreground-400">\u2014</span>
                      ) : !pos.sltpSetupDone ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded text-[10px] font-medium">
                          <i className="ri-loader-2-line animate-spin"></i>
                          Setting...
                        </span>
                      ) : pos.currentSL !== null && localParams.mode !== 'setupOnly' ? (
                        (() => {
                          const pip = Math.pow(10, -pos.digits + 1);
                          const distFromEntry = Math.abs(pos.currentSL - pos.entryPrice);
                          const pipsFromEntry = distFromEntry / pip;
                          const beThreshold = 1.5;
                          if (pipsFromEntry <= beThreshold) {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-500/10 text-accent-400 rounded text-[10px] font-medium">
                                <i className="ri-shield-check-line"></i>
                                BE
                              </span>
                            );
                          }
                          const trailPipsBeyondBE = pipsFromEntry - 1;
                          const stepNum = localParams.step > 0 ? Math.round(trailPipsBeyondBE / localParams.step) : 0;
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-[10px] font-medium">
                              <i className="ri-arrow-up-line"></i>
                              x{stepNum}
                            </span>
                          );
                        })()
                      ) : (
                        <span className="text-[10px] text-foreground-400">\u2014</span>
                      )}
                    </td>
                    <td className="text-center px-2 py-2.5">
                      {closingPositions.has(pos.positionId) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] text-foreground-400">
                          <i className="ri-loader-2-line animate-spin"></i>
                          Closing
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleClosePosition(pos.positionId)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                          title={`Close ${pos.symbolName} ${pos.direction.toUpperCase()}`}
                        >
                          <i className="ri-close-circle-line"></i>
                          Close
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ─── Alerts Log ─── */}
          {engineState?.alerts && engineState.alerts.length > 0 && (
            <div className="border-t border-background-200/70">
              <div className="px-4 py-2 bg-background-100 border-b border-background-200/50">
                <span className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide">Activity Log</span>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {engineState.alerts.slice(-20).reverse().map((alert) => (
                  <div
                    key={alert.id}
                    className="px-4 py-1.5 border-b border-background-200/30 text-[11px] text-foreground-600 flex items-center gap-2"
                  >
                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                      {new Date(alert.timestamp).toLocaleTimeString('en-US')}
                    </span>
                    <span className={cn(
                      'w-1.5 h-1.5 rounded-full flex-shrink-0',
                      alert.type === 'position_opened' && 'bg-amber-400',
                      alert.type === 'initial_sltp_set' && 'bg-emerald-400',
                      alert.type === 'breakeven' && 'bg-accent-400',
                      alert.type === 'sl_trailed' && 'bg-accent-400',
                      alert.type === 'tp_hit' && 'bg-secondary-400',
                      alert.type === 'position_closed' && 'bg-foreground-300',
                      alert.type === 'error' && 'bg-red-400',
                      alert.type === 'info' && 'bg-foreground-300',
                    )}></span>
                    <span>{alert.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}